import {
  FaBed,
  FaBoxOpen,
  FaCalendarAlt,
  FaCar,
  FaChartLine,
  FaClipboardList,
  FaCocktail,
  FaCompass,
  FaConciergeBell,
  FaLandmark,
  FaMapSigns,
  FaShoppingBag,
  FaStar,
  FaStore,
  FaTicketAlt,
  FaUtensils,
  FaWalking,
} from 'react-icons/fa';

const CATEGORY_ALIASES = {
  restaurant: 'restaurants',
  restaurants: 'restaurants',
  hotel: 'hotels',
  hotels: 'hotels',
  stay: 'hotels',
  stays: 'hotels',
  shopping: 'shopping',
  shop: 'shopping',
  activities: 'activities',
  activity: 'activities',
  attraction: 'attractions',
  attractions: 'attractions',
};

export function normalizeVendorCategory(category) {
  const normalized = String(category || '').trim().toLowerCase();
  return CATEGORY_ALIASES[normalized] || 'shopping';
}

export function getVendorDashboardPath(vendorOrCategory) {
  const category = typeof vendorOrCategory === 'string'
    ? vendorOrCategory
    : vendorOrCategory?.category || vendorOrCategory?.categoryId;

  return `/vendor-dashboard/${normalizeVendorCategory(category)}`;
}

export const VENDOR_DASHBOARD_CONFIG = {
  restaurants: {
    label: 'Restaurant Dashboard',
    heroTitle: 'Dining operations',
    heroDescription: 'Manage menu highlights, table demand, and guest-ready updates for your restaurant profile.',
    entityLabel: 'Menu Item',
    entityPlural: 'Menu Items',
    entityCollectionLabel: 'Menu',
    orderLabel: 'Reservations',
    revenueLabel: 'Dining Revenue',
    createLabel: 'Create menu listing',
    editLabel: 'Update menu listing',
    listHeading: 'Your menu highlights',
    emptyMessage: 'No menu items yet. Add your first featured dish or dining package.',
    analyticsMessage: 'Track best-performing cuisines, guest demand, and booking peaks across your restaurant listings.',
    ordersMessage: 'Review dining reservations and guest requests linked to your restaurant profile.',
    formLabels: {
      name: 'Dish or package name',
      category: 'Cuisine or meal type',
      price: 'Price',
      stock: 'Daily servings',
      imageUrl: 'Dish image URL',
      description: 'Dish description',
    },
    formPlaceholders: {
      name: 'Andhra thali experience',
      category: 'South Indian, desserts, breakfast',
      price: '499',
      stock: '60',
      imageUrl: 'https://...',
      description: 'Describe the dish, portions, special ingredients, or dining inclusions.',
    },
    sidebarItems: [
      { key: 'products', label: 'Menu', icon: FaUtensils },
      { key: 'analytics', label: 'Insights', icon: FaChartLine },
      { key: 'orders', label: 'Reservations', icon: FaClipboardList },
    ],
    highlights: [
      { icon: FaUtensils, label: 'Cuisine Spotlight' },
      { icon: FaCocktail, label: 'Popular Combos' },
      { icon: FaCalendarAlt, label: 'Peak Meal Slots' },
    ],
  },
  hotels: {
    label: 'Hotel Dashboard',
    heroTitle: 'Stay operations',
    heroDescription: 'Control room inventory, guest bookings, and service presentation for your property.',
    entityLabel: 'Room Listing',
    entityPlural: 'Room Listings',
    entityCollectionLabel: 'Rooms',
    orderLabel: 'Bookings',
    revenueLabel: 'Booking Revenue',
    createLabel: 'Create room listing',
    editLabel: 'Update room listing',
    listHeading: 'Your room inventory',
    emptyMessage: 'No room listings yet. Add your first stay package or room category.',
    analyticsMessage: 'Watch occupancy trends, room performance, and seasonal demand from one hospitality workspace.',
    ordersMessage: 'Review guest bookings and stay requests connected to your property.',
    formLabels: {
      name: 'Room or stay package',
      category: 'Room type',
      price: 'Nightly rate',
      stock: 'Rooms available',
      imageUrl: 'Room image URL',
      description: 'Room description',
    },
    formPlaceholders: {
      name: 'Deluxe valley view room',
      category: 'Suite, deluxe, villa',
      price: '4200',
      stock: '12',
      imageUrl: 'https://...',
      description: 'Describe amenities, view, occupancy, and check-in highlights.',
    },
    sidebarItems: [
      { key: 'products', label: 'Rooms', icon: FaBed },
      { key: 'analytics', label: 'Insights', icon: FaChartLine },
      { key: 'orders', label: 'Bookings', icon: FaClipboardList },
    ],
    highlights: [
      { icon: FaBed, label: 'Room Mix' },
      { icon: FaConciergeBell, label: 'Guest Services' },
      { icon: FaStar, label: 'Property Reputation' },
    ],
  },
  shopping: {
    label: 'Shopping Dashboard',
    heroTitle: 'Retail operations',
    heroDescription: 'Manage products, stock, and customer orders for your shopping storefront.',
    entityLabel: 'Product',
    entityPlural: 'Products',
    entityCollectionLabel: 'Catalogue',
    orderLabel: 'Orders',
    revenueLabel: 'Sales Revenue',
    createLabel: 'Create product listing',
    editLabel: 'Update product listing',
    listHeading: 'Your catalogue',
    emptyMessage: 'No products yet. Add your first listing from the form.',
    analyticsMessage: 'Track product activity, catalogue value, and listing performance from one quiet workspace.',
    ordersMessage: 'Review customer orders and purchase requests as they arrive in your store account.',
    formLabels: {
      name: 'Product name',
      category: 'Category',
      price: 'Price',
      stock: 'Stock',
      imageUrl: 'Image URL',
      description: 'Description',
    },
    formPlaceholders: {
      name: 'Handcrafted Kondapalli toy set',
      category: 'Souvenirs, fashion, gifts',
      price: '899',
      stock: '18',
      imageUrl: 'https://...',
      description: 'Describe the product, materials, and what makes it special.',
    },
    sidebarItems: [
      { key: 'products', label: 'Products', icon: FaBoxOpen },
      { key: 'analytics', label: 'Analytics', icon: FaChartLine },
      { key: 'orders', label: 'Orders', icon: FaClipboardList },
    ],
    highlights: [
      { icon: FaShoppingBag, label: 'Top Sellers' },
      { icon: FaStore, label: 'Store Health' },
      { icon: FaClipboardList, label: 'Order Queue' },
    ],
  },
  activities: {
    label: 'Activities Dashboard',
    heroTitle: 'Experience operations',
    heroDescription: 'Handle adventure listings, booking demand, and experience readiness for your activity brand.',
    entityLabel: 'Experience',
    entityPlural: 'Experiences',
    entityCollectionLabel: 'Experiences',
    orderLabel: 'Bookings',
    revenueLabel: 'Experience Revenue',
    createLabel: 'Create activity listing',
    editLabel: 'Update activity listing',
    listHeading: 'Your experiences',
    emptyMessage: 'No experiences yet. Add your first tour, adventure, or workshop.',
    analyticsMessage: 'Track schedule demand, guide utilization, and high-performing experiences in one place.',
    ordersMessage: 'Review upcoming bookings and traveller requests for your activities.',
    formLabels: {
      name: 'Experience name',
      category: 'Adventure type',
      price: 'Starting price',
      stock: 'Seats available',
      imageUrl: 'Experience image URL',
      description: 'Experience description',
    },
    formPlaceholders: {
      name: 'Sunrise kayaking tour',
      category: 'Outdoor adventure, water sport',
      price: '1499',
      stock: '24',
      imageUrl: 'https://...',
      description: 'Describe the route, duration, guide support, and skill expectations.',
    },
    sidebarItems: [
      { key: 'products', label: 'Experiences', icon: FaCompass },
      { key: 'analytics', label: 'Insights', icon: FaChartLine },
      { key: 'orders', label: 'Bookings', icon: FaClipboardList },
    ],
    highlights: [
      { icon: FaCompass, label: 'Adventure Mix' },
      { icon: FaWalking, label: 'Guide Flow' },
      { icon: FaCalendarAlt, label: 'Upcoming Slots' },
    ],
  },
  attractions: {
    label: 'Attractions Dashboard',
    heroTitle: 'Visitor operations',
    heroDescription: 'Oversee attraction passes, visitor demand, and destination presentation for your place.',
    entityLabel: 'Attraction Pass',
    entityPlural: 'Attraction Passes',
    entityCollectionLabel: 'Visitor Offers',
    orderLabel: 'Visitor Bookings',
    revenueLabel: 'Ticket Revenue',
    createLabel: 'Create visitor listing',
    editLabel: 'Update visitor listing',
    listHeading: 'Your visitor-ready offers',
    emptyMessage: 'No visitor offers yet. Add your first pass, guided visit, or featured listing.',
    analyticsMessage: 'Monitor visitor interest, on-site demand, and attraction visibility through one dashboard.',
    ordersMessage: 'Review visitor bookings and schedule requests tied to your attraction.',
    formLabels: {
      name: 'Pass or attraction name',
      category: 'Attraction type',
      price: 'Ticket price',
      stock: 'Daily capacity',
      imageUrl: 'Attraction image URL',
      description: 'Visitor description',
    },
    formPlaceholders: {
      name: 'Heritage guided walk',
      category: 'Historical site, museum, landmark',
      price: '299',
      stock: '120',
      imageUrl: 'https://...',
      description: 'Describe timings, visitor highlights, facilities, and tour inclusions.',
    },
    sidebarItems: [
      { key: 'products', label: 'Visitor Offers', icon: FaTicketAlt },
      { key: 'analytics', label: 'Insights', icon: FaChartLine },
      { key: 'orders', label: 'Bookings', icon: FaClipboardList },
    ],
    highlights: [
      { icon: FaLandmark, label: 'Visitor Appeal' },
      { icon: FaMapSigns, label: 'Destination Reach' },
      { icon: FaCar, label: 'On-site Flow' },
    ],
  },
};

export function getVendorDashboardConfig(category) {
  return VENDOR_DASHBOARD_CONFIG[normalizeVendorCategory(category)];
}
